import * as React from "react";
import {
  Alert,
  Card,
  Dialog,
  DialogContent,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import { DIALOG_WIDTH } from "../../constants";
import type { CloseDialogHandler } from "../../contexts/DialogContext";
import { useServiceMap } from "../../hooks/query/useServiceMap";
import { useAlerts } from "../../hooks/query/useAlerts";
import { useUpdateService } from "../../hooks/mutation/update/useUpdateService";
import { isCriticalKpiId } from "../../criticalKpi";
import { AlertsPicker } from "../AlertsPicker";
import StandardDialogActions from "./StandardDialogActions";
import StandardDialogTitle from "./StandardDialogTitle";

const unique = (values: string[]): string[] => {
  return Array.from(new Set(values.filter((value) => value.length > 0)));
};

const splitEvenPercent = (count: number): number[] => {
  if (count <= 0) {
    return [];
  }

  const base = Math.floor(100 / count);
  const remainder = 100 - base * count;

  return Array.from({ length: count }, (_, index) => {
    return base + (index < remainder ? 1 : 0);
  });
};

const ManageKpiAlertMappingsDialog: React.FC<{
  onDialogClosed: CloseDialogHandler;
  serviceMapId: string;
  serviceId: string;
}> = ({ onDialogClosed, serviceMapId, serviceId }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const { data: serviceMap = { nodes: [], edges: [] } } = useServiceMap(serviceMapId);
  const { data: alerts = [] } = useAlerts();
  const { mutateAsync: updateService, isPending: isUpdating } = useUpdateService();
  const [alertsByKpiId, setAlertsByKpiId] = React.useState<Record<string, string[]>>({});

  const service = React.useMemo(() => {
    return serviceMap.nodes.find((node) => node.id === serviceId)?.data;
  }, [serviceId, serviceMap.nodes]);

  React.useEffect(() => {
    if (!service) {
      return;
    }

    const nextAlertsByKpiId = Object.fromEntries(
      (service.kpis ?? []).map((kpi) => [kpi.id, unique(kpi.alerts ?? [])]),
    );
    setAlertsByKpiId(nextAlertsByKpiId);
  }, [service]);

  const onClose = () => {
    if (isUpdating) {
      return;
    }
    setSubmitError(null);
    onDialogClosed();
  };

  const onSave = async () => {
    if (!service) {
      setSubmitError("Service not found.");
      return;
    }

    try {
      setSubmitError(null);

      const updatedKpis = (service.kpis ?? []).map((kpi) => {
        const selectedAlertIds = unique(alertsByKpiId[kpi.id] ?? []);
        const isCritical = isCriticalKpiId(kpi.id);
        const distributedWeights = isCritical
          ? []
          : splitEvenPercent(selectedAlertIds.length);

        return {
          ...kpi,
          alerts: selectedAlertIds,
          alertConnections: selectedAlertIds.map((alertId, index) => ({
            alertId,
            weight: isCritical ? 0 : distributedWeights[index] ?? 0,
          })),
        };
      });

      await updateService({
        serviceMapId,
        systemId: serviceId,
        system: {
          kpis: updatedKpis,
        },
      });

      onDialogClosed({
        message: "KPI mappings updated!",
        severity: "success",
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Could not update KPI mappings.";
      setSubmitError(message);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{ sx: { maxWidth: isSmallScreen ? undefined : DIALOG_WIDTH.xl } }}
    >
      <StandardDialogTitle
        title="Manage"
        isSmallScreen={isSmallScreen}
        onClose={onClose}
      />

      <DialogContent sx={{ overflowX: "hidden" }}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Update KPI to alert mappings. New links are persisted directly on this service.
          </Typography>

          {submitError && <Alert severity="error">{submitError}</Alert>}

          {(service?.kpis ?? [])
            .slice()
            .sort((a, b) => {
              const aCritical = isCriticalKpiId(a.id);
              const bCritical = isCriticalKpiId(b.id);
              if (aCritical === bCritical) {
                return 0;
              }
              return aCritical ? -1 : 1;
            })
            .map((kpi) => {
            const selectedIds = alertsByKpiId[kpi.id] ?? [];
            const isCritical = isCriticalKpiId(kpi.id);

            return (
              <Card key={kpi.id} variant="glass" sx={{ p: 1.6 }}>
                <Stack spacing={1.1}>
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <HubRoundedIcon sx={{ fontSize: 14, color: "primary.main" }} />
                    <Typography sx={{ fontWeight: 700 }}>{kpi.name}</Typography>
                  </Stack>

                  <AlertsPicker
                    id={`manage-kpi-alerts-${kpi.id}`}
                    label="Alerts"
                    alerts={alerts}
                    width="100%"
                    value={selectedIds}
                    onChange={(next) => {
                      setAlertsByKpiId((prev) => ({
                        ...prev,
                        [kpi.id]: unique(next),
                      }));
                    }}
                  />

                  <Typography variant="body2" color="text.secondary">
                    {isCritical
                      ? "Critical KPI uses automatic weighting."
                      : "Alert weights are auto-distributed equally (sum = 100)."}
                  </Typography>
                </Stack>
              </Card>
            );
          })}
        </Stack>
      </DialogContent>

      <StandardDialogActions
        primaryText="Save"
        onCancel={onClose}
        onPrimaryClicked={() => {
          void onSave();
        }}
        loading={isUpdating}
        disabled={isUpdating || !service}
      />
    </Dialog>
  );
};

export default ManageKpiAlertMappingsDialog;
